import { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { useT, useLanguage } from '../i18n/LanguageContext'
import { useDocumentMeta } from '../hooks/useDocumentMeta'
import { getPosts, getCategories } from '../content/blog'
import PostCard from '../components/blog/PostCard'

export default function BlogIndex() {
  const t = useT()
  const { language } = useLanguage()
  useDocumentMeta(t.blog.meta.title, t.blog.meta.description)

  const posts = useMemo(() => getPosts(language), [language])
  const categories = useMemo(() => getCategories(language), [language])
  const [activeCategory, setActiveCategory] = useState<string | null>(null)

  const visible = activeCategory
    ? posts.filter((post) => post.category === activeCategory)
    : posts

  // Wyróżniamy najnowszy artykuł tylko w widoku pełnej listy. Przy włączonym
  // filtrze „najnowszy" wprowadzałby w błąd — sugerowałby ranking w obrębie
  // kategorii, a to wciąż zwykłe sortowanie po dacie.
  const [featured, ...rest] = visible
  const showFeatured = !activeCategory && visible.length > 2

  return (
    <section className="relative pt-40 pb-28 min-h-screen overflow-hidden">
      <div className="absolute inset-0 bg-grid" aria-hidden="true" />
      <div className="absolute inset-0 bg-radial-top" aria-hidden="true" />
      <div
        className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[700px] bg-brand-600/[0.05] rounded-full blur-[130px]"
        aria-hidden="true"
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <motion.header
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl mb-16"
        >
          <p className="font-mono text-accent-400 text-xs tracking-[0.2em] uppercase mb-5">
            {t.blog.eyebrow}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light text-white tracking-tight leading-[1.05] mb-6">
            {t.blog.titlePart1}{' '}
            <span className="text-gradient">{t.blog.titlePart2}</span>
          </h1>
          <p className="text-lg text-zinc-400 leading-relaxed">
            {t.blog.subtitle}
          </p>
        </motion.header>

        {categories.length > 1 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            role="group"
            aria-label={t.blog.filterAria}
            className="flex flex-wrap gap-2 mb-12"
          >
            <FilterChip
              label={t.blog.allCategories}
              active={activeCategory === null}
              onClick={() => setActiveCategory(null)}
            />
            {categories.map((category) => (
              <FilterChip
                key={category}
                label={category}
                active={activeCategory === category}
                onClick={() => setActiveCategory(category)}
              />
            ))}
          </motion.div>
        )}

        {visible.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/[0.12] bg-white/[0.02] px-8 py-20 text-center">
            <p className="text-zinc-500">{t.blog.empty}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {showFeatured && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.25 }}
              >
                <PostCard post={featured} featured />
              </motion.div>
            )}

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {(showFeatured ? rest : visible).map((post, i) => (
                <motion.div
                  key={post.slug}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 + i * 0.08 }}
                  className="flex"
                >
                  <PostCard post={post} />
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`px-4 py-2 rounded-lg text-sm border transition-all ${
        active
          ? 'bg-white/[0.1] border-white/[0.18] text-white'
          : 'bg-white/[0.03] border-white/[0.08] text-zinc-400 hover:text-white hover:border-white/[0.14]'
      }`}
    >
      {label}
    </button>
  )
}
